"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArtGallery } from "@/components/art-gallery";
import { FabricGallery } from "@/components/fabric-gallery";
import { MapPin, ExternalLink } from "lucide-react";

// Mosaic preview images from both sources for hero
const heroImages = [
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/earth-day-celebration-priscilla-wolfe.jpg",
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/vibrant-surreal-eye-collection-priscilla-wolfe.jpg",
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/majestic-peacock-priscilla-wolfe.jpg",
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/elegant-iris-watercolor-priscilla-wolfe.jpg",
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/urban-graffiti-peace-art-priscilla-wolfe.jpg",
  "https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/majestic-tiger-priscilla-wolfe.jpg",
];

export default function Home() {
  return (
    <main className="min-h-screen bg-[#0f0f0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#0f0f0f]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <span className="font-serif text-xl font-semibold text-white">Priscilla Wolfe</span>
            <span className="text-[#c9a84c] ml-2 text-sm hidden sm:inline">· Artist & Designer</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#fine-art" className="text-gray-400 hover:text-white text-sm transition-colors hidden md:block">Fine Art</a>
            <a href="#fabric-designs" className="text-gray-400 hover:text-white text-sm transition-colors hidden md:block">Fabric Designs</a>
            <a href="#contact" className="text-gray-400 hover:text-white text-sm transition-colors hidden md:block">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background mosaic */}
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-1 opacity-20">
          {heroImages.map((src, i) => (
            <div key={i} className="relative overflow-hidden">
              <Image
                src={src}
                alt=""
                fill
                className="object-cover"
                unoptimized
              />
            </div>
          ))}
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f0f] via-[#0f0f0f]/70 to-[#0f0f0f]/20" />

        {/* Hero content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-[#c9a84c]" />
              <span className="text-[#c9a84c] text-sm font-medium">Scottsdale, Arizona</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              Priscilla
              <span className="gradient-text"> Wolfe</span>
            </h1>
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              Artist, designer, and pattern creator blending hand-drawn artistry with
              digital innovation. From fine art prints to textile patterns — each piece
              tells a story.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-10">
              {[
                { value: "533+", label: "Fine Art Pieces" },
                { value: "873+", label: "Fabric Designs" },
                { value: "2015", label: "Year Joined" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="font-serif text-3xl font-bold gradient-text">
                    {stat.value}
                  </div>
                  <div className="text-gray-500 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                href="#fine-art"
                className="bg-[#c9a84c] text-black px-8 py-3 rounded-full font-semibold hover:bg-[#f5d78e] transition-colors"
              >
                View Gallery
              </a>
              <a
                href="#fabric-designs"
                className="border border-white/30 text-white px-8 py-3 rounded-full font-medium hover:border-[#c9a84c] hover:text-[#c9a84c] transition-colors"
              >
                Fabric Designs
              </a>
            </div>
          </motion.div>

          {/* Artist photo + featured image grid */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden md:grid grid-cols-2 gap-3"
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden col-span-1">
              <Image
                src="https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/vibrant-surreal-eye-collection-priscilla-wolfe.jpg"
                alt="Vibrant Surreal Eye Collection"
                fill
                className="object-cover"
                unoptimized
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <Image
                  src="https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/majestic-peacock-priscilla-wolfe.jpg"
                  alt="Majestic Peacock"
                  fill
                  className="object-cover"
                  unoptimized
                />
              </div>
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <Image
                  src="https://render.fineartamerica.com/images/images-profile-flow/400/images/artworkimages/mediumlarge/3/earth-day-celebration-priscilla-wolfe.jpg"
                  alt="Earth Day Celebration"
                  fill
                  className="object-cover"
                  unoptimized
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#c9a84c] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#c9a84c] text-sm font-semibold tracking-widest uppercase mb-4">About the Artist</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Hand-Drawn Artistry Meets Digital Innovation
          </h2>
          <div className="section-divider mb-8" />
          <p className="text-gray-400 text-lg leading-relaxed mb-8">
            I learned computer graphics and large format print when I owned a sign company.
            All of my art was created by hand. Recently I have added AI generated images created
            from my personal photos and memorabilia. I welcome special design projects — feel free
            to reach out for image licensing, image variations, color and size adjustments, or special requests.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://pixels.com/profiles/priscilla-wolfe"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#1e1e1e] border border-[#333] text-white px-6 py-3 rounded-full hover:border-[#c9a84c] hover:text-[#c9a84c] transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Pixels.com Profile
            </a>
            <a
              href="https://www.spoonflower.com/profiles/overt_ink"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#1e1e1e] border border-[#333] text-white px-6 py-3 rounded-full hover:border-[#c9a84c] hover:text-[#c9a84c] transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Spoonflower Shop
            </a>
          </div>
        </div>
      </section>

      {/* Fine Art Gallery */}
      <ArtGallery />

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />

      {/* Fabric Designs Gallery */}
      <FabricGallery />

      {/* Contact / Footer */}
      <footer id="contact" className="py-16 px-6 bg-[#080808] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="font-serif text-2xl font-bold text-white mb-3">Priscilla Wolfe</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Artist & Designer based in Scottsdale, Arizona.
                Creating art and patterns that tell a story.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Shop My Work</h4>
              <ul className="space-y-3">
                <li>
                  <a href="https://pixels.com/profiles/priscilla-wolfe" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Fine Art — Pixels.com
                  </a>
                </li>
                <li>
                  <a href="https://priscilla-wolfe.pixels.com/" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Personal Pixels Shop
                  </a>
                </li>
                <li>
                  <a href="https://www.spoonflower.com/profiles/overt_ink" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Fabric & Wallpaper — Spoonflower
                  </a>
                </li>
                <li>
                  <a href="https://overtink.myspreadshop.com/" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Apparel — Spreadshirt
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Connect</h4>
              <ul className="space-y-3">
                <li>
                  <a href="https://instagram.com/overtinkart" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Instagram @overtinkart
                  </a>
                </li>
                <li>
                  <a href="https://pinterest.com/pjwpvaz" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Pinterest
                  </a>
                </li>
                <li>
                  <a href="https://pixels.com/sendemail.html?artistid=367612" target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#c9a84c] transition-colors text-sm flex items-center gap-2">
                    <ExternalLink className="w-3 h-3" /> Send a Message
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 text-center">
            <p className="text-gray-600 text-sm">
              © 2024 Priscilla Wolfe. All artwork is the original work of Priscilla Wolfe.
              Images sourced from{" "}
              <a href="https://pixels.com/profiles/priscilla-wolfe" className="text-[#c9a84c] hover:underline" target="_blank" rel="noopener noreferrer">Pixels.com</a>
              {" "}and{" "}
              <a href="https://www.spoonflower.com/profiles/overt_ink" className="text-[#c9a84c] hover:underline" target="_blank" rel="noopener noreferrer">Spoonflower</a>.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}
